import {
  BulkAssignmentsFinanceCostCenterMembershipDocument,
  BulkAssignmentsFinanceCostCenterMembershipMembershipsToCreateInput,
  BulkAssignmentsFinanceCostCenterMembershipMembershipsToUpdateInput,
  BulkCreateFinanceCostCenterMembershipDocument,
  BulkCreateFinanceCostCenterMembershipMembershipsInput,
  BulkDeleteFinanceCostCenterMembershipDocument,
} from 'generated/resources/graphql'
import { noop } from 'lodash'

import { useMutation } from 'modules/core/hooks/useMutation'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface CreateProps {
  assignments: BulkCreateFinanceCostCenterMembershipMembershipsInput[]
}

interface SaveProps {
  assignmentsToCreate: BulkAssignmentsFinanceCostCenterMembershipMembershipsToCreateInput[]
  assignmentsToUpdate: BulkAssignmentsFinanceCostCenterMembershipMembershipsToUpdateInput[]
}

interface DeleteProps {
  ids: number[]
}

export const useCostCentersMutations = () => {
  const [bulkCreateFinanceCostCenterMembership] = useMutation(
    BulkCreateFinanceCostCenterMembershipDocument,
    {
      extractorFn: (newData) => newData.financeMutations.bulkCreateFinanceCostCenterMembership,
    }
  )

  const [bulkDeleteFinanceCostCenterMembership] = useMutation(
    BulkDeleteFinanceCostCenterMembershipDocument,
    {
      extractorFn: (newData) => newData.financeMutations.bulkDeleteFinanceCostCenterMembership,
    }
  )

  const [bulkAssignmentsFinanceCostCenterMembership] = useMutation(
    BulkAssignmentsFinanceCostCenterMembershipDocument,
    {
      extractorFn: (newData) => newData.financeMutations.bulkAssignmentsFinanceCostCenterMembership,
    }
  )

  const manageAction = (response: UnhandledResult<Result<unknown>>, successMessage?: string) => {
    const result = handleResult(response, noop)

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: successMessage || i18n.t('finance.cost_center_detail.sidepanel.success'),
      })
    }

    return result.success
  }

  const create = async ({ assignments }: CreateProps) => {
    const response = await bulkCreateFinanceCostCenterMembership({
      variables: {
        memberships: assignments,
      },
    })

    return manageAction(response, i18n.t('finance.cost_centers.assignments.create.success'))
  }

  const save = async ({ assignmentsToCreate, assignmentsToUpdate }: SaveProps) => {
    const response = await bulkAssignmentsFinanceCostCenterMembership({
      variables: {
        membershipsToCreate: assignmentsToCreate,
        membershipsToUpdate: assignmentsToUpdate,
      },
    })

    return manageAction(response)
  }

  const deleteAssignments = async ({ ids }: DeleteProps) => {
    const response = await bulkDeleteFinanceCostCenterMembership({
      variables: { ids },
    })

    return manageAction(response, i18n.t('finance.cost_centers.assignments.delete_modal.success'))
  }

  return {
    create,
    save,
    deleteAssignments,
  }
}
